import { logger } from "../../logger";
import { logPile } from "../logPile";
import {
  SiteTableItem,
  SiteTableKeyComponents,
  SitePartitionName,
  ArtData,
  ChallengeCreateInput,
  ChallengeLoginInput,
  ChallengeChangePasswordInput,
  SearchInput,
  PatchCheckoutRequest,
  PatchCheckoutResponse,
  GetCheckoutRequest,
  GetCheckoutResponse,
} from "../../../types";
import { Image } from "../../../types/image";
import {
  UserCreateParams,
  UserLoginParams,
  UserUpdateParams,
} from "../../../types/user";
import { Errors } from "../../../shared-utils";

export const getToken = () => {
  const auth = window?.localStorage?.getItem?.("auth") ?? undefined;
  return auth;
};

// Every API call leaves a breadcrumb (method, path, status) so a capture's
// timeline shows what the app was doing. Bodies are deliberately not recorded.
export const authFetch = async (
  url: Parameters<typeof fetch>[0],
  props: Parameters<typeof fetch>[1],
  getAuth: () => string | undefined = getToken,
) => {
  const token = getAuth();
  const method = props?.method ?? "GET";
  const path = (() => {
    try {
      return new URL(String(url), window.location.origin).pathname;
    } catch {
      return String(url);
    }
  })();

  try {
    const res = await fetch(url, {
      ...props,
      headers: {
        ...(token ? { authorization: token } : {}),
        ...props?.headers,
      },
    });
    logPile.addBreadcrumb({
      category: "http",
      level: res.ok ? "info" : "warning",
      message: `${method} ${path} → ${res.status}`,
      data: { status: res.status },
    });
    return res;
  } catch (e) {
    logPile.addBreadcrumb({
      category: "http",
      level: "error",
      message: `${method} ${path} failed`,
    });
    throw e;
  }
};
export const Api = {
  getAll: async <DataType extends SiteTableItem>(
    {
      type,
    }: {
      type: SitePartitionName;
    },
    getToken?: () => string | undefined,
  ): Promise<
    | {
        success: true;
        body: { items: DataType[]; total: number };
        error?: undefined;
      }
    | { success: false; error: string; body?: undefined }
  > => {
    const getURL = new URL(`/api/${type}`, import.meta.env.VITE_SITE_URL);
    try {
      const res = await authFetch(
        getURL,
        {
          method: "GET",
        },
        getToken,
      );

      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },
  get: async <DataType extends SiteTableItem>(
    { type, slug }: SiteTableKeyComponents,
    getToken?: () => string | undefined,
  ): Promise<
    | { success: true; data: DataType; error?: undefined; code?: undefined }
    | { success: false; data?: undefined; error: string; code: string }
  > => {
    const getURL = new URL(
      `/api/${type}/${slug}`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await authFetch(
        getURL,
        {
          method: "GET",
        },
        getToken,
      );
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      return {
        success: false,
        error: "unknown",
        code: Errors.INTERNAL_SERVICE_ERROR,
      };
    }
  },

  getRelated: async <DataType extends SiteTableItem>({
    type,
    slug,
    subType,
  }: {
    type: SitePartitionName;
    slug: string;
    subType: SitePartitionName;
  }): Promise<
    | {
        success: true;
        body: { items: DataType[]; total: number };
        error?: undefined;
      }
    | { success: false; error: string; body?: undefined }
  > => {
    const getRelatedURL = new URL(
      `/api/${type}/${slug}/related/${subType}`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await authFetch(getRelatedURL, {
        method: "GET",
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      return { success: false, error: "unknown" };
    }
  },

  search: async <DataType extends SiteTableItem>({
    type,
    ...data
  }: SearchInput): Promise<
    | {
        success: true;
        body: { items: DataType[]; total: number };
        error?: undefined;
      }
    | { success: false; error: string; body?: undefined }
  > => {
    const searchUrl = new URL(
      `/api/${type}/search`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await authFetch(searchUrl, {
        method: "POST",
        body: JSON.stringify(data),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },

  update: async <DataType extends SiteTableItem>(
    data: Partial<DataType>,
    getToken?: () => string | undefined,
  ): Promise<{ success: boolean } & any> => {
    const updateUrl = new URL(
      `/api/${data.type}/${data.slug}`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await authFetch(
        updateUrl,
        {
          method: "PUT",
          body: JSON.stringify(data),
        },
        getToken,
      );
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },

  create: async <DataType extends SiteTableItem>(
    data: DataType,
  ): Promise<{ success: boolean } & any> => {
    const createUrl = new URL(
      `/api/${data.type}/${data.slug}/`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await authFetch(createUrl, {
        method: "POST",
        body: JSON.stringify(data),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },

  delete: async ({
    type,
    slug,
    ...data
  }: SiteTableKeyComponents): Promise<{ success: boolean } & any> => {
    const url = new URL(`/api/${type}/${slug}/`, import.meta.env.VITE_SITE_URL);
    url.search = new URLSearchParams(data).toString();
    try {
      const res = await authFetch(url, {
        method: "DELETE",
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },
};

export const AuthApi = {
  create: async (
    user: UserCreateParams,
  ): Promise<{ success: boolean; token: string } & any> => {
    const createUrl = new URL(`/auth/create`, import.meta.env.VITE_SITE_URL);
    try {
      const res = await fetch(createUrl, {
        method: "POST",
        body: JSON.stringify(user),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },
  login: async (user: UserLoginParams) => {
    const loginUrl = new URL(`/auth/login`, import.meta.env.VITE_SITE_URL);
    try {
      const res = await fetch(loginUrl, {
        method: "POST",
        body: JSON.stringify(user),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },

  createAuthChallenge: async (
    params: ChallengeCreateInput,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    const createChallengeUrl = new URL(
      `/auth/challenge`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await fetch(createChallengeUrl, {
        method: "POST",
        body: JSON.stringify(params),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },
  loginWithChallenge: async (
    params: ChallengeLoginInput,
  ): Promise<{ success: boolean; token: string } & any> => {
    const loginChallengeUrl = new URL(
      `/auth/challenge/login`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await fetch(loginChallengeUrl, {
        method: "POST",
        body: JSON.stringify(params),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },

  changePassword: async (
    params: ChallengeChangePasswordInput,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    const validateUrl = new URL(
      `/auth/challenge/changePassword`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await fetch(validateUrl, {
        method: "POST",
        body: JSON.stringify(params),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },
  validateChallenge: async (
    params: Pick<ChallengeChangePasswordInput, "code" | "email">,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    const validateUrl = new URL(
      `/auth/challenge/changePassword`,
      import.meta.env.VITE_SITE_URL,
    );
    try {
      const res = await fetch(validateUrl, {
        method: "POST",
        body: JSON.stringify(params),
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },
  getUser: async (token: string) => {
    // const getUserUrl = new URL(`/auth/getUser`, import.meta.env.VITE_SITE_URL);
    const getUserUrl = new URL(`/auth/user`, import.meta.env.VITE_SITE_URL);

    try {
      const res = await fetch(getUserUrl, {
        // method: "post",
        // body: JSON.stringify({ token }),
        method: "get",
        headers: { authorization: token },
      });
      const body = await res.json();
      return body;
    } catch (e) {
      logger(e);
      console.log(e);
      return { success: false, error: "unknown" };
    }
  },

  updateUser: async (
    params: UserUpdateParams,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    const patchUrl = new URL(`/auth/user`, import.meta.env.VITE_SITE_URL);
    const res = await authFetch(patchUrl, {
      method: "PATCH",
      body: JSON.stringify(params),
    });

    const body = await res.json();

    return body;
  },
};

export const ImageApi = {
  getSignedUrl: async (name: string) => {
    const requestPutUrl = new URL(
      `/image/upload`,
      import.meta.env.VITE_SITE_URL,
    );
    const response = await fetch(requestPutUrl, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    const result = (await response.json()) as {
      success: boolean;
    } & (
      | {
          success: true;
          url: string;
          storedName: string;
          type: "image/png" | "image/jpg" | "image/jpeg";
        }
      | {
          success: false;
          error: string;
          info: any;
        }
    );

    if (result.success === false) {
      logger("error: ", result);
      throw Error(result.error);
    } else {
      return { signedUrl: result.url, storedName: result.storedName };
    }
  },
  create: async (image: Image) => {
    const { signedUrl, storedName } = await ImageApi.getSignedUrl(image.name);

    let binary = atob(image.data.split(",")[1]);

    let array = [];
    for (var i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    let blobData = new Blob([new Uint8Array(array)], { type: "image/jpeg" });
    const response = await fetch(signedUrl, {
      method: "PUT",
      body: blobData,
    });
    const imageUrl = signedUrl.split("?")[0];

    const result = await response.text();
    const status = await response.status;
    return {
      status,
      imageUrl,
      result,
      storedName,
    };
  },
};

export const ArtApi = {
  ...Api,
  isViewable: <DataType extends ArtData>(data: DataType): boolean => {
    if (data.isPublic === true) {
      return false;
    }
    return true;
  },
};

export const CheckoutApi = {
  patchCheckout: async (
    body: PatchCheckoutRequest,
  ): Promise<PatchCheckoutResponse> => {
    const patchCheckoutUrl = new URL(
      `/payment/checkout/${body.id}`,
      import.meta.env.VITE_SITE_URL,
    );

    const response = await fetch(patchCheckoutUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    const jsonResult: PatchCheckoutResponse = await response.json();
    return jsonResult;
  },
  getCheckout: async (
    body: GetCheckoutRequest,
  ): Promise<GetCheckoutResponse> => {
    const getCheckoutUrl = new URL(
      `/payment/checkout/${body.id}`,
      import.meta.env.VITE_SITE_URL,
    );
    const response = await authFetch(getCheckoutUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const jsonResult: GetCheckoutResponse = await response.json();
    return jsonResult;
  },
};
